import React, {useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import Layout from './Layout';
import Container from 'components/Container';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Icon from '@mdi/react';
import SGTitle from 'components/Typography/SGTitle';
import {mdiCashRegister, mdiTable} from '@mdi/js';
import TabPanel from 'components/Tabs/TabPanel';
import Paper from 'components/Paper';
import LayoutHeader, {LayoutHeaderIconTypes} from 'components/LayoutHeader';
import POSStateProvider, {
  usePOSState,
  posActionTypes,
} from 'features/POSRegisters/components/POSState';
import usePOS from 'features/POSRegisters/hooks/usePOS';
import POSLoadingIndicator from 'features/POSRegisters/components/POSLoadingIndicator';
import AlertsDrawer from 'features/POSRegisters/components/AlertsDrawer';
import POSDiagram from 'features/POSRegisters/POSDiagram';
import POSTableContainer from 'features/POSRegisters/POSTable/POSTableContainer';
import Landing from 'features/POSRegisters/POSLanding/Landing';
import useSiteSelection from 'features/POSRegisters/hooks/useSiteSelection';
import useDevice from 'features/SiteSearch/hooks/useDevice';
import RedirectSwitchParams from 'features/POSRegisters/POSTable/SwitchConnectivity/interfaces/RedirectSwitchParams';
import POSResponseData from 'features/POSRegisters/interfaces/POSResponseData';
import SiteSearch from 'features/POSRegisters/components/SiteSearch';
import DebugJsonTextArea from 'components/DebugJsonTextArea';
import {useUserInfoState} from 'hooks/useUserInfo/UserInfoProvider';

//#region styled-components
const StyledContainer = styled(Container)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding-top: 32px;
`;

const StyledPaper = styled(Paper)`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 228px);
`;

const StyledSwitchHeaderTab = styled.div`
  min-height: 16px;
`;

const StyledTabs = styled(Tabs)`
  margin-top: 8px;
  .MuiTabs-indicator {
    height: 6px;
    background-color: ${props => props.theme.activeStateBlue};
  }
`;

const StyledTab = styled(Tab)`
  margin-bottom: 15px;
  min-width: 217px;
  padding: 0px 16px;
  transition: 200ms;
  margin-right: 1.5rem;
  &.MuiTab-textColorInherit {
    color: ${props => props.theme.text};
    border-radius: 10px;
    border: 4px solid ${props => props.theme.grey};
  }
  &.MuiTab-textColorInherit.Mui-selected {
    background-color: ${props => props.theme.exportButtonBg || props.theme.blue2};
    color: ${props => props.theme.black};
    border: 4px solid ${props => props.theme.exportButtonBg || props.theme.blue2};
  }
  &.MuiTab-labelIcon {
    min-height: 56px;
  }
  .MuiTab-wrapper {
    flex-direction: row;
    column-gap: 0.5rem;
  }
  .MuiTab-wrapper > *:first-child {
    margin-bottom: 4px;
  }
`;

const StyledLayout = styled(Layout)`
  .header-root {
    margin-bottom: 0;
  }
  @media (min-width: 600px) {
    .header-root {
      margin-bottom: 0;
    }
  }
`;
//#endregion styled-components

function POSRegisters() {
  // #region state
  const {siteSelected, queryErrors} = useSiteSelection();
  const siteId = siteSelected?.siteId ?? null;
  const {
    state: {debugMode},
  } = useUserInfoState();

  // POS Data
  const {state, dispatch: reactDispatch} = usePOSState();
  const {data: posData, dataLoaded, error: posFetchingError} = usePOS(
    siteSelected?.isTestSite ? undefined : siteSelected?.siteId
  );
  const [currentTab, setCurrentTab] = useState(0);

  // Devices Data
  const {data: devicesData} = useDevice(siteId ?? '');
  const deviceNames = devicesData?.map(device => device.deviceName) ?? null;

  const switchRedirectInfo: RedirectSwitchParams = {
    siteId,
    deviceNames,
    siteType: siteSelected?.siteType,
  };
  // #endregion state

  // #region functions
  const dispatchUpdatePOSData = useCallback((posData: POSResponseData) => {
    reactDispatch({
      type: 'UPDATE_POS_DATA',
      payload: posData,
    });
  }, []);

  const dispatchUpdateSiteId = useCallback((siteId: string | null) => {
    reactDispatch({
      type: 'UPDATE_SITE_ID',
      payload: siteId,
    });
  }, []);

  const dispatchUseTestSite = useCallback(() => {
    reactDispatch({
      type: posActionTypes.USE_TEST_SITE,
    });
  }, []);

  const dispatchResetPOSData = useCallback(() => {
    reactDispatch({
      type: posActionTypes.RESET_POS_DATA,
    });
  }, []);

  const updatePOSWithTestData = useCallback(
    (json: string) => {
      const testData = JSON.parse(json) as POSResponseData;
      dispatchUpdatePOSData(testData);
    },
    [dispatchUpdatePOSData]
  );
  // #endregion functions

  // #region useEffect
  useEffect(() => {
    if (siteSelected?.isTestSite === true) {
      dispatchUseTestSite();
    } else if (!dataLoaded && posData === null) {
      dispatchResetPOSData();
    } else if (dataLoaded && posData !== null) {
      dispatchUpdatePOSData(posData);
    }
  }, [
    siteSelected,
    dataLoaded,
    posData,
    dispatchUpdatePOSData,
    dispatchUseTestSite,
    dispatchResetPOSData,
  ]);

  // #endregion useEffect

  return (
    <StyledLayout
      headerContent={
        <>
          <LayoutHeader
            title={'Nike POS'}
            icon={LayoutHeaderIconTypes.Registers}
            searchComponent={<SiteSearch />}
          />
          {siteSelected !== undefined && (
            <StyledSwitchHeaderTab>
              <SGTitle variant={'t2'} className={'header-title-color'}>
                {siteSelected?.siteCode} - {siteSelected?.siteName}
              </SGTitle>
            </StyledSwitchHeaderTab>
          )}
        </>
      }
    >
      {siteSelected === undefined ? (
        <Landing />
      ) : (
        <POSLoadingIndicator
          key={`${siteId ? siteId.toString() : ''}-${siteSelected?.siteCode}-${
            siteSelected?.siteName
          }`}
          siteId={siteSelected.siteId ?? undefined}
          posFetchingError={!!posFetchingError}
          isLoaded={state.isPOSDataReady}
        >
          <StyledContainer>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <StyledPaper id="pos-wrapper">
                  <Grid container spacing={2}>
                    <Grid item xs={10}>
                      <StyledTabs
                        value={currentTab}
                        onChange={(event: unknown, newValue: number) => setCurrentTab(newValue)}
                      >
                        <StyledTab
                          icon={<Icon path={mdiCashRegister} size={1} />}
                          label="DIAGRAM"
                        />
                        <StyledTab icon={<Icon path={mdiTable} size={1} />} label="TABLE" />
                      </StyledTabs>
                    </Grid>

                    <AlertsDrawer state={state} />

                    <Grid item xs={12}>
                      <TabPanel value={currentTab} index={0}>
                        <POSDiagram state={state} siteId={siteId} />
                      </TabPanel>
                      <TabPanel value={currentTab} index={1}>
                        <POSTableContainer state={state} switchRedirectInfo={switchRedirectInfo} />
                      </TabPanel>
                    </Grid>
                  </Grid>
                </StyledPaper>
              </Grid>
              {siteSelected?.isTestSite && debugMode && (
                <Grid item xs={12}>
                  <DebugJsonTextArea
                    handleUpdateJson={updatePOSWithTestData}
                    handleUseNonDebug={dispatchResetPOSData}
                    ariaLabel="NPOS Debug JSON Override"
                  />
                </Grid>
              )}
            </Grid>
          </StyledContainer>
        </POSLoadingIndicator>
      )}
    </StyledLayout>
  );
}

export default function () {
  return (
    <POSStateProvider>
      <POSRegisters />
    </POSStateProvider>
  );
}
